import React, { useContext } from "react";
import { Box, CircularProgress, Typography } from "@material-ui/core";
import { useTheme } from "@material-ui/styles";
import { InformationMessage } from "nemuru-components";
import IconTitle from "../../../../../shared/components/IconTitle";
import Text from "../../../../../shared/components/Text";
import { DocumentIdCapture } from "./DocumentIdCapture";
import { DocumentIdPictureFooter } from "./DocumentIdPictureFooter";
import { ProcessContext } from "../../../../../../contexts/ProcessContext";
import { useDocumentIdPictureMedia } from "../hooks/useDocumentIdPictureMedia";
import { useDocumentIdPictureCameraFlow } from "../hooks/useDocumentIdPictureCameraFlow";
import { useDocumentIdPictureStepErrors } from "../hooks/useDocumentIdPictureStepErrors";
import { useTranslation } from "../../../../../../utils/hooks/useTranslation";
import { CAPTURE_MODES, getDocumentIdDocumentLabel } from "../../../config";

export const DocumentIdPictureCameraMode = ({ documentSide, onSubmitForm }) => {
  const { palette } = useTheme();
  const {
    state: { participant, idDocumentVerification },
    dispatch,
  } = useContext(ProcessContext);
  const { getTextIdTranslation } = useTranslation();

  const onboardingDetailsId = participant.onboardingDetails?.id;
  const documentType = idDocumentVerification.documentType;
  const captureMode = CAPTURE_MODES.CAMERA;

  const {
    captureRef,
    captureState,
    handleCaptureStateChange,
    isUploadMode,
    selectedBase64,
    hasSelectedPicture,
    subtitle,
    takePicture,
  } = useDocumentIdPictureMedia({ captureMode, documentSide, documentType });

  const {
    invalidPhotoError,
    processExpiredError,
    genericError,
    retriableVerifyError,
    hasError,
    continueDisabled,
  } = useDocumentIdPictureStepErrors({
    idDocumentVerification,
    isUploadMode,
    hasSelectedPicture,
    selectedBase64,
  });

  const {
    isVerifying,
    uploadingDocumentation,
    handleRestartProcess,
    handleContinueWithPhoto,
    handleUploadRetry,
    handleCameraRetry,
  } = useDocumentIdPictureCameraFlow({
    documentSide,
    dispatch,
    onSubmitForm,
    onboardingDetailsId,
    idDocumentVerification,
    selectedBase64,
    captureRef,
  });

  const isLoading = isVerifying || uploadingDocumentation;
  const canRenderMedia = !hasError && !isLoading;

  return (
    <>
      <Box className="appear-anim">
        <IconTitle
          title={
            <Text
              tId="DOCUMENT_ID_VERIFICATION"
              dynamicDataTexts={[getDocumentIdDocumentLabel(documentType)]}
            />
          }
          subtitle={<Text tId={subtitle} />}
        />
      </Box>
      <Box>
        {canRenderMedia && !isUploadMode && (
          <>
            <Typography
              variant={"body2"}
              color={"textPrimary"}
              style={{ marginBottom: 16 }}
            >
              <Text tId={`PHOTO_ID_DOCUMENT_${documentSide}`} /> (
              <Text
                tId={"STEP_X_OF_Y"}
                dynamicDataTexts={[documentSide === "front" ? "1" : "2", "2"]}
              />
              ):
            </Typography>
            <DocumentIdCapture
              ref={captureRef}
              onStateChange={handleCaptureStateChange}
            />
            {captureState.cameraError ? (
              <Box mt={2}>
                <InformationMessage
                  variant={{
                    type: "error",
                    icon: (
                      <i
                        className="material-icons-outlined"
                        style={{ fontSize: 22 }}
                      >
                        videocam_off
                      </i>
                    ),
                  }}
                  isScreenXs={false}
                  messageContent={{
                    title: <Text tId="ID_DOC_CAMERA_ERROR_TITLE" />,
                    body: <Text tId="ID_DOC_CAMERA_ERROR_BODY" />,
                  }}
                />
              </Box>
            ) : null}
            <Box mt={2} display={"flex"} justifyContent={"flex-start"}>
              {selectedBase64 ? (
                <Box display="flex" gridGap={6} alignItems="start">
                  <i
                    className="material-icons"
                    style={{
                      fontSize: "24px",
                      color: palette.primary.main,
                      lineHeight: "24px",
                    }}
                  >
                    {"fact_check"}
                  </i>
                  <Typography
                    style={{ color: palette.text.title }}
                    variant="body2"
                  >
                    <Text tId={"PHOTO_WILL_BE_UPLOADED_FOR_VERIFICATION"} />
                  </Typography>
                </Box>
              ) : (
                <Box display="flex" gridGap={6} alignItems="start">
                  <i
                    className="material-icons"
                    style={{
                      fontSize: "24px",
                      color: palette.primary.main,
                      lineHeight: "24px",
                    }}
                  >
                    {"center_focus_weak"}
                  </i>
                  <Typography
                    style={{ color: palette.text.title }}
                    variant="body2"
                  >
                    <Text tId={"PROCESS_INFO_BULLET_FRAME"} />
                  </Typography>
                </Box>
              )}
            </Box>
          </>
        )}
        {isLoading && (
          <>
            <Box>
              <Box
                display="flex"
                flexDirection="column"
                alignItems="center"
                justifyContent="center"
                py={4}
              >
                <CircularProgress color="primary" />
                <Box mt={2}>
                  <Typography
                    align="center"
                    variant="body2"
                    style={{ color: palette.text.title }}
                  >
                    <Text
                      tId={
                        uploadingDocumentation
                          ? "UPLOADING_DOCUMENT_PICTURE"
                          : "VERIFYING_DOCUMENT"
                      }
                    />
                  </Typography>
                </Box>
              </Box>
            </Box>
            <Box height={10} />
          </>
        )}
        {processExpiredError && (
          <InformationMessage
            variant={{
              type: "error",
              icon: (
                <i className="material-icons-outlined" style={{ fontSize: 22 }}>
                  timer_off
                </i>
              ),
            }}
            isScreenXs={false}
            messageContent={{
              title: <Text tId="ID_DOC_UPLOAD_PROCESS_EXPIRED_TITLE" />,
              body: <Text tId="ID_DOC_UPLOAD_PROCESS_EXPIRED_BODY" />,
            }}
          />
        )}
        {invalidPhotoError && (
          <InformationMessage
            variant={{
              type: "error",
              icon: (
                <i className="material-icons-outlined" style={{ fontSize: 22 }}>
                  error_outline
                </i>
              ),
            }}
            isScreenXs={false}
            messageContent={{
              title: (
                <Text
                  tId="ID_DOC_UPLOAD_INVALID_PHOTO_TITLE"
                  dynamicDataTexts={[getTextIdTranslation(documentSide)]}
                />
              ),
              body: <Text tId="ID_DOC_UPLOAD_INVALID_PHOTO_BODY" />,
            }}
          />
        )}
        {genericError && (
          <InformationMessage
            variant={{
              type: "error",
              icon: (
                <i className="material-icons-outlined" style={{ fontSize: 22 }}>
                  error_outline
                </i>
              ),
            }}
            isScreenXs={false}
            messageContent={{
              title: <Text tId="ID_DOC_UPLOAD_GENERIC_ERROR_TITLE" />,
              body: (
                <Text
                  tId={
                    retriableVerifyError
                      ? "ID_DOC_VERIFY_GENERIC_ERROR_BODY"
                      : "ID_DOC_UPLOAD_CAPTURED_GENERIC_ERROR_BODY"
                  }
                />
              ),
            }}
          />
        )}
      </Box>
      <Box>
        <DocumentIdPictureFooter
          processExpiredError={processExpiredError}
          isLoading={isLoading}
          isUploadMode={isUploadMode}
          hasSelectedPicture={hasSelectedPicture}
          captureVideoReady={captureState.videoReady}
          onRestartProcess={handleRestartProcess}
          onTakePicture={takePicture}
          onUploadRetry={handleUploadRetry}
          onCameraRetry={handleCameraRetry}
          onContinueWithPhoto={handleContinueWithPhoto}
          continueDisabled={continueDisabled}
          documentSide={documentSide}
        />
      </Box>
    </>
  );
};
